import React, { useState, useEffect } from 'react';
import ShareModal from '../common/ShareModal';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  XMarkIcon, 
  CalendarDaysIcon, 
  MapPinIcon, 
  CurrencyRupeeIcon, 
  ClockIcon, 
  TagIcon, 
  UserIcon, 
  ShareIcon,
  HeartIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserPlusIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import { Collaboration, CollaborationCollaborator, ReviewModel, Portfolio } from '../../types';
import Button from '../common/Button';
import MasonryCollabGrid from '../profile/MasonryCollabGrid';
import PortfolioViewModalV2 from '../profile/PortfolioViewModalV2';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import CreateCollaborationModal from './CreateCollaborationModal';
import { collaborationService, reviewsAPI } from '../../services/api';
import { toast } from 'react-hot-toast';
import CollaboratorReviewSection from './CollaboratorReviewSection';
import { createReview } from '../../store/slices/reviewsSlice';
import Avatar from '../common/Avatar';
import { getPreferredImageUrl } from '../../utils/imageUtils';
import { formatCurrency } from '../../utils/format';

interface CollaborationDetailModalProps {
  collaboration: Collaboration;
  onClose: () => void;
}

const CollaborationDetailModal: React.FC<CollaborationDetailModalProps> = ({
  collaboration,
  onClose
}) => {
  const [userCollaborator, setUserCollaborator] = useState<CollaborationCollaborator | null>(null);
  const { user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const navigate = useNavigate();

  // Confirmation Modal State
  const [confirmationAction, setConfirmationAction] = useState<'accept' | 'reject' | null>(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [reviews, setReviews] = useState<{[key: string]: {rating: number, comment: string}}>({});
  const [responding, setResponding] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [showPortfolioModal, setShowPortfolioModal] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  useEffect(() => {
    const currentUser = collaboration.collaborators?.find(c => c.user_id === user?.id);
    setUserCollaborator(currentUser || null);
  }, [collaboration, user?.id]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleReviewChange = (collaboratorId: string, field: 'rating' | 'comment', value: any) => {
    setReviews(prev => ({
      ...prev,
      [collaboratorId]: {
        ...prev[collaboratorId],
        [field]: value
      }
    }));
  };

  const handleConfirmAction = async () => {
    if (!confirmationAction) return;
    setResponding(true);

    try {
      // 1. Submit Reviews if any
      const collabortorsReviews: ReviewModel[] = [];
      for (const [collaboratorId, review] of Object.entries(reviews)) {
        if (collaboratorId && collaboratorId !== 'null' && collaboratorId !== 'undefined' && review.rating > 0) {
          const reviewModel: ReviewModel = {
            reviewee_id: collaboratorId,
            booking_id: collaboration.id,
            rating: review.rating,
            comment: review.comment
          };
          collabortorsReviews.push(reviewModel);
        }
      }

      if (collabortorsReviews.length > 0) {
        await dispatch(createReview(collabortorsReviews)).unwrap();
      }

      // 2. Submit Approval/Rejection
      const approved = confirmationAction === 'accept';
      await collaborationService.submitApproval(collaboration.id, approved, responseMessage);
      
      toast.success(approved ? 'Invitation accepted!' : 'Invitation rejected');
      setConfirmationAction(null);
      onClose();
      window.location.reload(); // Refresh to show updates
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    } finally {
      setResponding(false);
    }
  };

  const [existingReviews, setExistingReviews] = useState<any[]>([]);

  useEffect(() => {
    if (confirmationAction) {
      reviewsAPI.getMyReviews()
        .then((response) => {
          if (response.data && response.data.data && response.data.data.reviews) {
             setExistingReviews(response.data.data.reviews);
          }
        })
        .catch((err) => console.error('Failed to fetch existing reviews', err));
    }
  }, [confirmationAction]);

  const renderConfirmationModal = () => {
    if (!confirmationAction) return null;

    const isAccepting = confirmationAction === 'accept';
    
    // Filter out collaborators who have already been reviewed
    const collaboratorsToReview = (collaboration.collaborators || []).filter(
      c => c.user_id !== user?.id && 
      !existingReviews.some((r: any) => 
        (r.reviewee_id === c.user_id || r.revieweeId === c.user_id) && 
        (r.booking_id === collaboration.id || r.bookingId === collaboration.id)
      )
    );

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-100 dark:border-gray-800">
          <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center sticky top-0 bg-white dark:bg-gray-900 z-10">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              {isAccepting ? 'Accept Collaboration' : 'Reject Collaboration'}
            </h3>
            <button 
              onClick={() => setConfirmationAction(null)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
            >
              <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Confirmation Message */}
            <div className={`p-4 rounded-lg flex items-start gap-3 ${
              isAccepting ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400' : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
            }`}>
              {isAccepting ? (
                <CheckCircleIcon className="w-6 h-6 shrink-0" />
              ) : (
                <XCircleIcon className="w-6 h-6 shrink-0" />
              )}
              <div>
                <p className="font-medium">
                  {isAccepting 
                    ? "You are about to accept this collaboration." 
                    : "You are about to reject this collaboration."}
                </p>
                <p className="text-sm opacity-90 mt-1">
                  {isAccepting 
                    ? "This will notify the creator and other collaborators. You can also leave a review for your experience."
                    : "The creator will be notified. Please provide a reason below."}
                </p>
              </div>
            </div>

            {/* Message Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {isAccepting ? "Message (Optional)" : "Reason for Rejection (Required)"}
              </label>
              <textarea
                value={responseMessage}
                onChange={(e) => setResponseMessage(e.target.value)}
                placeholder={isAccepting 
                  ? "Share your excitement or next steps..." 
                  : "Please explain why you're declining..."}
                className="w-full px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none h-32"
              />
            </div>

            {/* Collaborator Reviews - Only if Accepting and there are collaborators to review */}
            {isAccepting && (
                <>
                {collaboratorsToReview.length > 0 ? (
                  <CollaboratorReviewSection
                    collaborators={collaboratorsToReview}
                    reviews={reviews}
                    onReviewChange={handleReviewChange}
                    currentUser={user}
                  />
                ) : (
                    (collaboration.collaborators?.length || 0) > 1 && (
                        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg text-center text-gray-500 dark:text-gray-400 text-sm">
                            You have already reviewed all collaborators.
                        </div>
                    )
                )}
                </>
            )}
          </div>

          <div className="p-6 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 flex justify-end gap-3 sticky bottom-0">
            <button
              onClick={() => setConfirmationAction(null)}
              className="px-6 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 font-medium hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              disabled={responding}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmAction}
              disabled={responding || (!isAccepting && !responseMessage.trim())}
              className={`px-6 py-2.5 rounded-lg text-white font-medium shadow-lg shadow-primary-500/20 transition-all flex items-center gap-2 ${
                isAccepting 
                  ? 'bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600'
                  : 'bg-red-500 hover:bg-red-600'
              } ${responding || (!isAccepting && !responseMessage.trim()) ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {responding ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  {isAccepting ? 'Confirm & Accept' : 'Confirm Rejection'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'open':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'pending':
        return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800/50';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800/50';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800/50';
      case 'completed':
        return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800/50';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700';
    }
  };

  const isOwner = collaboration.creator_id === user?.id;

  const displayImages = (collaboration.images || []).map((img, idx) => 
    getPreferredImageUrl(img, collaboration.medium_images?.[idx], user?.image_quality_preference)
  );

  const coverImage = getPreferredImageUrl(
    collaboration.cover_image || '', 
    collaboration.medium_cover_image, 
    user?.image_quality_preference
  );

  // Construct viewable portfolio items for the modal
  const viewablePortfolios: Portfolio[] = (collaboration.images || []).map((img, idx) => ({
    id: `collab-img-${collaboration.id}-${idx}`,
    user_id: collaboration.creator_id,
    title: collaboration.title,
    description: collaboration.project_description,
    image_url: img,
    thumbnail_image_url: collaboration.thumbnail_images?.[idx] || img,
    medium_image_url: collaboration.medium_images?.[idx] || img,
    tags: collaboration.tags || [],
    likes: 0,
    views: 0,
    comments_count: 0,
    shares: 0,
    created_at: collaboration.created_at,
    updated_at: collaboration.updated_at,
    collaborators: collaboration.collaborators?.map(c => c.user || {
      id: c.user_id || '',
      first_name: c.name.split(' ')[0],
      last_name: c.name.split(' ').slice(1).join(' '),
      user_type: 'creator', // Fallback
      profile_pic: c.profile_pic,
      is_profile_complete: true,
      completion_score: 100
    } as any) || [],
    user: collaboration.creator,
    is_collaboration: true,
    collaboration_id: collaboration.id
  }));

  // Add cover image if it's not in the images list (optional, but good for completeness)
  // For now, we'll stick to the images list as that's what MasonryCollabGrid shows.
  // If the user clicks the hero image (cover), we can try to find it in the list or just show the first image.
  
  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index);
    setShowPortfolioModal(true);
  };

  const handleHeroImageClick = () => {
    // If cover image is one of the images, find its index. Otherwise default to 0.
    // For simplicity, we'll just open the first image for now, or we could add the cover image to the list.
    // Let's just open index 0.
    setSelectedImageIndex(0);
    setShowPortfolioModal(true);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[80]"
        onClick={handleBackdropClick}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-5xl h-[90vh] overflow-hidden flex flex-col border border-gray-100 dark:border-gray-800"
        >
          {/* Hero Section */}
          <div 
            className="relative h-[60vh] sm:h-[400px] flex-shrink-0 group overflow-hidden cursor-pointer"
            onClick={handleHeroImageClick}
          >
            {collaboration.cover_image ? (
              <img
                src={coverImage}
                alt={collaboration.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            ) : displayImages.length > 0 ? (
              <img
                src={displayImages[0]}
                alt={collaboration.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-teal-500 to-emerald-600" />
            )}
            
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent dark:from-gray-950 dark:via-gray-950/40 dark:to-transparent" />

            {/* Top Actions */}
            <div className="absolute top-6 right-6 flex items-center space-x-3 z-10">
              <button 
                onClick={() => setShowShareModal(true)}
                className="p-2.5 bg-black/20 dark:bg-gray-900/40 backdrop-blur-md rounded-full text-white hover:bg-black/40 dark:hover:bg-gray-900/60 transition-all duration-200 border border-white/10 dark:border-gray-800"
              >
                <ShareIcon className="h-5 w-5" />
              </button>
              <button 
                onClick={onClose}
                className="p-2.5 bg-black/20 dark:bg-gray-900/40 backdrop-blur-md rounded-full text-white hover:bg-black/40 dark:hover:bg-gray-900/60 transition-all duration-200 border border-white/10 dark:border-gray-800 group-hover:rotate-90"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Content Overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
              <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
                <div className="space-y-3 max-w-3xl">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold tracking-wide border ${getStatusColor(collaboration.status)}`}>
                      {collaboration.status === 'open' ? 'LIVE' : collaboration.status.replace('_', ' ').toUpperCase()}
                    </span>
                    {collaboration.is_paid && (
                      <span className="bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-400 border border-amber-200 dark:border-amber-800/50 px-3 py-1 rounded-full text-xs font-semibold tracking-wide flex items-center gap-1">
                        <CurrencyRupeeIcon className="h-3 w-3" />
                        PAID PROJECT
                      </span>
                    )}
                  </div>
                  
                  <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white tracking-tight leading-tight">
                    {collaboration.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-6 text-gray-200 dark:text-gray-300 text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <UserIcon className="h-4 w-4 text-teal-400" />
                      <span>{collaboration.creator_role}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPinIcon className="h-4 w-4 text-teal-400" />
                      <span>{collaboration.place}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CalendarDaysIcon className="h-4 w-4 text-teal-400" />
                      <span>{new Date(collaboration.start_date).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>

                {isOwner && (
                  <Button
                    onClick={() => setShowUpdateModal(true)}
                    className="bg-teal-600 text-white hover:bg-teal-700 border-none shadow-lg font-semibold px-6 py-2.5 rounded-xl transition-all transform hover:-translate-y-1 flex-shrink-0"
                  >
                    Edit Project
                  </Button>
                )}
              </div>
            </div>

            {/* Mobile Details Toggle */}
            <button
              onClick={() => setIsDetailsOpen(!isDetailsOpen)}
              className="lg:hidden absolute bottom-4 right-4 p-3 bg-white/20 backdrop-blur-md rounded-full text-white z-20 border border-white/20 shadow-lg"
            >
              {isDetailsOpen ? <ChevronUpIcon className="w-6 h-6" /> : <ChevronDownIcon className="w-6 h-6" />}
              <span className="sr-only">Toggle Details</span>
            </button>
          </div>

          {/* Main Content Area */}
          <div className={`flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-950 ${isDetailsOpen ? 'block' : 'hidden lg:block'}`}>
            <div className="max-w-7xl mx-auto p-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
                {/* Left Column: Main Content */}
                <div className="lg:col-span-2 space-y-8">
                  
                  {/* Description */}
                  <section className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-800">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      Project Description
                    </h3>
                    <div className="prose prose-teal dark:prose-invert max-w-none text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-line">
                      {collaboration.project_description}
                    </div>
                  </section>

                  {/* Tags */}
                  {collaboration.tags && collaboration.tags.length > 0 && (
                    <section className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-800">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                        <TagIcon className="h-5 w-5 text-teal-500" />
                        Tags & Skills
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {collaboration.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="px-4 py-2 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-700 hover:border-teal-200 dark:hover:border-teal-800 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors cursor-default"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </section>
                  )}

                  {/* Insights */}
                  {collaboration.creator_insights && (
                    <section className="bg-gradient-to-br from-teal-50 to-emerald-50 dark:from-teal-900/20 dark:to-emerald-900/20 rounded-2xl p-6 border border-teal-100 dark:border-teal-800/50">
                      <h3 className="text-xl font-bold text-teal-900 dark:text-teal-100 mb-4">Creator Insights</h3>
                      <p className="text-teal-800 dark:text-teal-200 italic leading-relaxed">
                        "{collaboration.creator_insights}"
                      </p>
                    </section>
                  )}

                  {/* Collaborators Section */}
                  <section>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                      <UserIcon className="h-6 w-6 text-teal-600 dark:text-teal-400" />
                      The Team
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {collaboration.collaborators && collaboration.collaborators.length > 0 ? (
                        [...collaboration.collaborators]
                          .sort((a, b) => {
                            // Sort owner to the top
                            if (a.user_id === collaboration.creator_id) return -1;
                            if (b.user_id === collaboration.creator_id) return 1;
                            return 0;
                          })
                          .map((collaborator) => (
                          <div 
                            key={collaborator.id} 
                            onClick={() => {
                              if (collaborator.is_portal_user && collaborator.user_id) {
                                onClose();
                                navigate(`/profile/${collaborator.user_id}`);
                              }
                            }}
                            className={`rounded-2xl p-5 shadow-sm border transition-all flex items-start gap-4 relative overflow-hidden ${
                              collaborator.is_portal_user && collaborator.user_id ? 'cursor-pointer hover:shadow-md' : ''
                            } ${
                              isOwner 
                                ? (collaborator.approval_status === 'rejected' 
                                    ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/50' 
                                    : collaborator.approval_status === 'approved'
                                      ? 'bg-emerald-50/50 dark:bg-emerald-900/10 border-emerald-100 dark:border-emerald-800/30'
                                      : 'bg-white dark:bg-gray-900 border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50')
                                : 'bg-white dark:bg-gray-900 border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                            }`}
                          >
                            {/* Status Indicator Strip - Owner Only */}
                            {isOwner && (
                              <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${
                                collaborator.approval_status === 'rejected' ? 'bg-red-500' :
                                collaborator.approval_status === 'approved' ? 'bg-emerald-500' :
                                'bg-amber-400 dark:bg-amber-500'
                              }`} />
                            )}

                            <div className={`flex-shrink-0 ${isOwner ? 'pl-2' : ''}`}>
                              <Avatar
                                src={collaborator.user?.profile_pic_thumb_signed || collaborator.user?.profile_pic || collaborator.profile_pic}
                                alt={collaborator.name}
                                size="lg"
                                className={`border-2 shadow-sm ${
                                  isOwner 
                                    ? (collaborator.approval_status === 'rejected' ? 'border-red-200 dark:border-red-800/50' :
                                       collaborator.approval_status === 'approved' ? 'border-emerald-200 dark:border-emerald-800/50' :
                                       'border-white dark:border-gray-800')
                                    : 'border-white dark:border-gray-800'
                                }`}
                                isDeleted={!!collaborator.user?.deleted_at || !!collaborator.user?.deletion_scheduled_at}
                              />
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <h4 className={`text-base font-bold leading-tight ${
                                    isOwner && collaborator.approval_status === 'rejected' ? 'text-red-900 dark:text-red-100' : 'text-gray-900 dark:text-white'
                                  }`}>
                                    {collaborator.name}
                                  </h4>
                                  <p className={`font-medium text-sm mt-0.5 ${
                                    isOwner && collaborator.approval_status === 'rejected' ? 'text-red-700 dark:text-red-400' : 'text-teal-600 dark:text-teal-400'
                                  }`}>
                                    {collaborator.role}
                                  </p>
                                </div>
                                
                                {/* Status Icon - Owner Only */}
                                {isOwner && (
                                  <div className="flex-shrink-0 pt-0.5" title={collaborator.approval_status}>
                                    {collaborator.approval_status === 'approved' && (
                                      <CheckCircleIcon className="h-6 w-6 text-emerald-500 dark:text-emerald-400" />
                                    )}
                                    {collaborator.approval_status === 'rejected' && (
                                      <XCircleIcon className="h-6 w-6 text-red-500 dark:text-red-400" />
                                    )}
                                    {collaborator.approval_status === 'pending' && (
                                      <ClockIcon className="h-6 w-6 text-amber-400 dark:text-amber-500" />
                                    )}
                                  </div>
                                )}
                              </div>
                              
                              {collaborator.insights && (
                                <div className={`mt-2 text-xs italic p-2 rounded-lg ${
                                  isOwner && collaborator.approval_status === 'rejected' 
                                    ? 'bg-red-100/50 dark:bg-red-900/30 text-red-800 dark:text-red-200 border border-red-100 dark:border-red-800/50' 
                                    : 'bg-gray-50 dark:bg-gray-800/50 text-gray-500 dark:text-gray-400'
                                }`}>
                                  "{collaborator.insights}"
                                </div>
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="col-span-full text-center py-8 bg-white dark:bg-gray-900 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700">
                          <p className="text-gray-500 dark:text-gray-400 font-medium">No collaborators yet.</p>
                        </div>
                      )}
                    </div>
                  </section>
                </div>



                {/* Right Column: Sticky Sidebar */}
                <div className="lg:col-span-1">
                  <div className="sticky top-8 space-y-6">
                    
                    {/* Collaborator Actions - Only for invited user */}
                    {userCollaborator && !isOwner && (
                      <div className={`rounded-2xl p-6 shadow-sm border ${
                        userCollaborator.approval_status === 'pending' ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-100 dark:border-amber-800/50' :
                        userCollaborator.approval_status === 'approved' ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-100 dark:border-emerald-800/50' :
                        'bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-800/50'
                      }`}>
                        <h3 className={`text-lg font-bold mb-2 ${
                          userCollaborator.approval_status === 'pending' ? 'text-amber-900 dark:text-amber-100' :
                          userCollaborator.approval_status === 'approved' ? 'text-emerald-900 dark:text-emerald-100' :
                          'text-red-900 dark:text-red-100'
                        }`}>
                          {userCollaborator.approval_status === 'pending' ? 'Pending Invitation' :
                           userCollaborator.approval_status === 'approved' ? 'Invitation Accepted' :
                           'Invitation Rejected'}
                        </h3>
                        
                        {userCollaborator.approval_status === 'pending' ? (
                          <div className="space-y-3">
                            <p className="text-sm text-amber-800 dark:text-amber-200">
                              You have been invited to join this project as <strong>{userCollaborator.role}</strong>.
                            </p>
                            <div className="flex gap-3">
                              <Button
                                onClick={() => setConfirmationAction('accept')}
                                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white border-none"
                              >
                                Accept
                              </Button>
                              <Button
                                onClick={() => setConfirmationAction('reject')}
                                className="flex-1 bg-red-500 hover:bg-red-600 text-white border-red-500 shadow-sm"
                              >
                                Reject
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <p className={`text-sm ${
                            userCollaborator.approval_status === 'approved' ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'
                          }`}>
                            You have {userCollaborator.approval_status} this invitation.
                            {userCollaborator.approval_status === 'rejected' && (
                              <button 
                                onClick={() => setConfirmationAction('accept')}
                                className="block mt-2 text-xs font-semibold underline hover:text-red-900 dark:hover:text-red-100"
                              >
                                Change to Accept?
                              </button>
                            )}
                          </p>
                        )}
                      </div>
                    )}

                    <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-800 space-y-6">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-800 pb-4">
                        Project Details
                      </h3>
                      
                      <div className="space-y-5">
                        {collaboration.is_paid && (
                          <div className="flex items-start gap-4">
                            <div className="p-2 bg-amber-50 dark:bg-amber-900/30 rounded-lg text-amber-600 dark:text-amber-400">
                              <CurrencyRupeeIcon className="h-6 w-6" />
                            </div>
                            <div>
                              <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">Budget</p>
                              <p className="text-gray-900 dark:text-white font-semibold text-lg">
                                {formatCurrency(collaboration.budget)}
                              </p>
                            </div>
                          </div>
                        )}

                        <div className="flex items-start gap-4">
                          <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg text-blue-600 dark:text-blue-400">
                            <ClockIcon className="h-6 w-6" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">Duration</p>
                            <p className="text-gray-900 dark:text-white font-semibold">{collaboration.duration}</p>
                          </div>
                        </div>

                        <div className="flex items-start gap-4">
                          <div className="p-2 bg-purple-50 dark:bg-purple-900/30 rounded-lg text-purple-600 dark:text-purple-400">
                            <CalendarDaysIcon className="h-6 w-6" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">Timeline</p>
                            <p className="text-gray-900 dark:text-white font-semibold">
                              {new Date(collaboration.start_date).toLocaleDateString()} - {new Date(collaboration.end_date).toLocaleDateString()}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-start gap-4">
                          <div className="p-2 bg-rose-50 dark:bg-rose-900/30 rounded-lg text-rose-600 dark:text-rose-400">
                            <MapPinIcon className="h-6 w-6" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">Location</p>
                            <p className="text-gray-900 dark:text-white font-semibold">{collaboration.place}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Gallery Section - Full Width */}
              <section className="border-t border-gray-200 dark:border-gray-800 pt-10">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 flex items-center gap-3">
                  <ClockIcon className="h-7 w-7 text-teal-600 dark:text-teal-400" />
                  Project Gallery
                </h3>
                <div className="bg-white dark:bg-gray-900 rounded-2xl p-4 shadow-sm border border-gray-100 dark:border-gray-800 min-h-[400px]">
                  {displayImages.length > 0 ? (
                    <MasonryCollabGrid 
                      collabImages={displayImages} 
                      isOwnProfile={false} 
                      onImageClick={handleImageClick}
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center h-64 text-gray-400 dark:text-gray-500">
                      <div className="w-16 h-16 bg-gray-50 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                        <ShareIcon className="w-8 h-8 opacity-20" />
                      </div>
                      <p className="font-medium">No images in gallery</p>
                    </div>
                  )}
                </div>
              </section>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {showUpdateModal && (
        <CreateCollaborationModal
          mode="update"
          collaboration={collaboration}
          onClose={() => setShowUpdateModal(false)}
          onSuccess={() => {
            setShowUpdateModal(false);
            onClose();
            window.location.reload(); // Refresh to sync updated details and prevent stale errors
          }}
        />
      )}
      {renderConfirmationModal()}
      {/* Share Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        title={collaboration.title}
        url={`${window.location.origin}/collaborations/${collaboration.id}`}
        type="collaboration"
        data={collaboration}
      />

      {/* Portfolio View Modal for Image Preview */}
      {showPortfolioModal && viewablePortfolios.length > 0 && (
        <PortfolioViewModalV2
          portfolioId={viewablePortfolios[selectedImageIndex]?.id || viewablePortfolios[0].id}
          onClose={() => setShowPortfolioModal(false)}
          portfolios={viewablePortfolios}
          source="profile" // or 'feed', doesn't matter much since we pass portfolios
        />
      )}
    </AnimatePresence>
  );
};

export default CollaborationDetailModal;
